import React, { useEffect, useState } from 'react';
import styles from './LeftSidebarPrototype.module.scss';
import fullLogo from '../../../assets/logo_blue_black.svg';
import smallLogo from '../../../assets/logo_grad.svg';
import SidebarOption from './sidebar/SidebarOption';
import { Icon } from '@mui/material';
import { useSpring, animated } from 'react-spring';
import { useLocation, useNavigate } from 'react-router-dom';
import { Text } from '../../../components/typography';
import { useReactiveVar } from '@apollo/client';
import { navigationExpandedVar } from '../../../graphql/cache/navbar';
import classNames from 'classnames';
import { useCurrentUser } from '../../../providers/UserProvider';
import { paths } from '../../../constants/strings';
import { useOnlineStatus } from '../../../hooks/offline-hooks/offline-misc';
import { companyScopedKeys } from '../../../constants/strings';
import { useMobileListener, useQueryParams } from '../../../hooks/misc';
import TextWithIcon from '../../../components/typography/TextWithIcon';
import { useTranslation } from 'react-i18next';
import { customColors } from '../../../utilities';
import Spacer from '../../../components/typography/Spacer';
import { getTranslationKey } from '../../../utilities/translator/translation_keys';
import { useURLParams } from '../../../providers/URLParamProvider';

export default function LeftSidebarPrototype({
  collapsed,
  setCollapsed,
  options,
  bottomOptions,
  dashboardOption,
}) {
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const { user: currentUser } = useCurrentUser();
  const navigationExpanded = useReactiveVar(navigationExpandedVar);
  const { getParam, upsertParams, deleteParams } = useQueryParams();
  const { segment } = useURLParams();
  const isMobile = useMobileListener();
  const { t } = useTranslation();
  const location = useLocation();
  const [showChildren, setShowChildren] = useState(false);
  const { width } = useSpring({
    config: { duration: 500 },
    from: { width: '14rem' },
    width: collapsed ? '4.375rem' : '14rem',
  });

  const [hideText, setHideText] = useState(collapsed);

  useEffect(() => {
    if (collapsed && !hideText) {
      setHideText(true);
    } else if (!collapsed && hideText) {
      setTimeout(() => {
        setHideText(false);
      }, 500);
    }
  }, [collapsed, hideText]);

  function isSelected(b) {
    const segments = window.location.pathname.split('/');
    const type = (getParam('type') || '').toLowerCase();
    const tab = (getParam('tab') || '').toLowerCase();
    const idx = companyScopedKeys.includes(b) ? 1 : 3;

    const a = (segments[idx] || '').toLowerCase();
    const bb = (b || '').toLowerCase();
    if (a === 'audits' && (tab === 'cpa' || tab === 'cpas')) {
      if (bb === 'cpa' || bb === 'cpas') return true;
      if (bb === 'audit' || bb === 'audits') return false;
    }
    if (
      (type === 'audit' || type === 'incident') &&
      (bb === 'assessment' || bb === 'assessments')
    ) {
      return false;
    }
    if (a === bb || a + 's' === bb || a === bb + 's') return true;
    if (type === 'audit' && (bb === 'audit' || bb === 'audits')) return true;
    if (
      type === 'incident' &&
      (bb === 'incident' || bb === 'incidents') &&
      !companyScopedKeys.includes(b)
    )
      return true;

    return false;
  }
  const show = (key) => key === showChildren;

  useEffect(() => {
    if (segment && segment !== 'assessments' && segment !== 'actionitems') {
      setShowChildren(null);
    }
  }, [segment]);

  return (
    <div className={!navigationExpanded ? styles.hide : null}>
      <animated.div
        id="left-sidebar-prototype"
        style={{ width }}
        className={styles[`container${collapsed ? 'Collapsed' : ''}`]}
      >
        <div className={styles.top}>
          <div
            className={
              collapsed ? styles.collapsedLogoContainer : styles.logoContainer
            }
          >
            {collapsed ? (
              <img
                onClick={() => navigate('/')}
                src={smallLogo}
                className={styles.smallLogo}
                alt="opas mobile icon"
              />
            ) : (
              <img
                onClick={() => navigate('/')}
                src={fullLogo}
                className={styles.logo}
                alt="opas mobile logo"
              />
            )}
            <Spacer />
            <Icon
              className={styles.collapseIcon}
              onClick={() => {
                setShowChildren(null);
                setCollapsed(!collapsed);
              }}
              sx={{ color: customColors.SECONDARY }}
            >
              {collapsed ? 'menu' : 'menu_open'}
            </Icon>
            {!!collapsed && (
              <Text
                noMargin
                size="xs"
                textAlign="center"
                color="secondary"
              >{`V${currentUser.frontVersion}`}</Text>
            )}
          </div>
          <div className={styles.hr} />
          <div
            className={classNames([
              styles[`projectContainer${collapsed ? 'Collapsed' : ''}`],
            ])}
          >
            <div
              className={styles.userContainer}
              onClick={() => {
                if (isOnline) {
                  navigate(`/${paths.user}/${currentUser?.id}`);
                  navigationExpandedVar(false);
                }
              }}
            >
              <img
                src={currentUser?.avatarUrl}
                className={styles.avatar}
                alt={currentUser?.firstName}
              />
              {hideText ? null : (
                <div>
                  <div className={styles.right}>
                    <Text
                      noSelect
                      weight="bold"
                      noMargin
                      truncate
                      className={styles.userName}
                    >
                      {currentUser?.firstName} {currentUser?.lastName}
                    </Text>
                    <Text
                      noSelect
                      weight="semiBold"
                      noMargin
                      color="secondary"
                      truncate
                      size="sm"
                    >
                      {currentUser?.company?.name}
                    </Text>
                    <Text
                      noMargin
                      size="xs"
                      color="secondary"
                    >{`V${currentUser.frontVersion}`}</Text>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.hr} />
        <div className={!collapsed ? styles.middle : styles.middleCollapsed}>
          <SidebarOption
            collapsed={collapsed}
            key={dashboardOption.title}
            option={dashboardOption}
            selected={isSelected(dashboardOption.param)}
            onSelect={() => {
              navigate(dashboardOption.path);
              navigationExpandedVar(false);
            }}
            title={dashboardOption.title}
            icon={dashboardOption.icon}
          />
          {isOnline
            ? Object.entries(options).map(([key, value]) => {
                const hideSection = value.every((v) => !v.show);
                if (hideSection) return null;
                const display = show(key);
                return (
                  <div key={`${key}-${value}`}>
                    {!collapsed && (
                      <div className={styles.flexRowSm}>
                        <Text
                          size="xs"
                          weight="semiBold"
                          color="secondary"
                          noMargin
                        >
                          {t(getTranslationKey(key, 'dashboard')) || key}
                        </Text>

                        <div className={styles.line} />
                      </div>
                    )}
                    {value.map((option) => {
                      if (!option?.show) return null;
                      return (
                        <div className={styles.spaceBetween} key={option.title}>
                          <div>
                            <SidebarOption
                              option={option}
                              collapsed={collapsed}
                              key={option.title}
                              selected={
                                option.lockedType
                                  ? isSelected(option.param) &&
                                    getParam('lockedType') === option.lockedType
                                  : isSelected(option.param) &&
                                    !(
                                      getParam('lockedType') &&
                                      option.param === 'assessments'
                                    )
                              }
                              showChildren={display}
                              onSelect={() => {
                                if (
                                  showChildren === option.param ||
                                  collapsed
                                ) {
                                  setShowChildren(null);
                                } else {
                                  setShowChildren(option.param);
                                }

                                if (option.path) {
                                  navigate(option.path);
                                }
                                if (option.onClick) {
                                  option.onClick(option.title);
                                }
                                navigationExpandedVar(false);
                              }}
                              title={option.title}
                              icon={option.icon}
                            />
                            {!!option?.children?.length &&
                            !isMobile &&
                            !!isSelected(option.param) &&
                            showChildren === option.param
                              ? option.children?.map((c) => {
                                  const isSelected =
                                    getParam([option.urlParam]) === c.key;

                                  return (
                                    <div
                                      key={c.key}
                                      className={
                                        isSelected
                                          ? styles.smallOptionSelected
                                          : styles.smallOption
                                      }
                                    >
                                      <TextWithIcon
                                        size="xs"
                                        hover
                                        truncate
                                        leftIcon={c.icon}
                                        iconProps={{
                                          color: isSelected
                                            ? customColors.BLUE
                                            : c.color,
                                          fontSize: '1rem',
                                        }}
                                        color={
                                          isSelected
                                            ? 'accentPrimary'
                                            : 'primary'
                                        }
                                        weight="semiBold"
                                        onClick={() => {
                                          const params = new URLSearchParams(
                                            location.search,
                                          );
                                          const removeParams = [
                                            '',
                                            'undefined',
                                            'null',
                                          ];

                                          for (const [key, value] of Array.from(
                                            params.entries(),
                                          )) {
                                            if (removeParams.includes(value)) {
                                              params.delete(key);
                                            }
                                          }
                                          if (!!c?.onClick) {
                                            c.onClick();
                                          } else {
                                            if (!!option.urlParam)
                                              deleteParams(['tab']);
                                            let tab =
                                              option?.param === 'actionitems'
                                                ? 'open'
                                                : option.param;
                                            upsertParams({
                                              tab,
                                              [option.urlParam]: c.key,
                                            });
                                          }
                                        }}
                                      >
                                        {!collapsed
                                          ? t(c.title) || c.title || c.name
                                          : null}
                                      </TextWithIcon>
                                    </div>
                                  );
                                })
                              : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })
            : null}
        </div>
        <div className={styles.bottom}>
          {bottomOptions.map((option) => (
            <div key={option.title}>
              <SidebarOption
                option={option}
                collapsed={collapsed}
                key={option.title}
                selected={
                  window.location.pathname.split('/')[1] === option.param
                }
                onSelect={() => {
                  if (option.path) {
                    navigate(option.path);
                  }
                  if (option.onClick) {
                    option.onClick(option.title);
                  }
                  navigationExpandedVar(false);
                }}
                title={option.title}
                icon={option.icon}
              />
            </div>
          ))}
        </div>
      </animated.div>
    </div>
  );
}
